import { useCallback, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useMediaQuery } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Helmet } from "react-helmet";
import ContainerPage from "../../components/Containers/ContainerPage";
import TitlePage from "../../components/Texts/TitlePage";
import Button from "../../components/Buttons/Button";
import ModalCreateAutomation from "../../components/Modal/ModalCreateAutomation/ModalCreateAutomation";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { getNameBusinessTheme, getRoleCapability } from "../../actions/getters";
import {
  AUTOMATIONS_NO_PERMISSION,
  AUTOMATIONS_PAGE_DESCRIPTION,
  AUTOMATIONS_PAGE_TITLE,
  BUTTON_NEW_AUTOMATION,
} from "../../i18n/keysTranslations";
import { usePublishingAutomations } from "../../hooks/usePublishingAutomations";
import useAutomationsAddon from "../../hooks/useAutomationsAddon";
import FeatureAddonPaywallDialog from "../../components/FeatureAddons/FeatureAddonPaywallDialog";
import FeatureAddonUnpaidPaywall from "../../components/FeatureAddons/FeatureAddonUnpaidPaywall";
import { FEATURE_ADDON_KEYS } from "../../actions/featureAddons";
import { useContentModals } from "../../contexts/contentModalsContext";
import { summarizeHeroMetrics } from "../../utils/publishingAutomationMappers";
import AutomationHeroCard from "./components/AutomationHeroCard";
import AutomationCard from "./components/AutomationCard";
import { buildOptimisticSwitchCopy } from "../../optimisticSwitch/copy";
import { OPTIMISTIC_SWITCH_KIND } from "../../optimisticSwitch/kinds";
import {
  runOptimisticSwitch,
  toOptimisticSwitchPayload,
} from "../../optimisticSwitch/runOptimisticSwitch";

const Automations = () => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { theme } = useSelectorApp((state) => state.ui);
  const isMobile = useMediaQuery((muiTheme) => muiTheme.breakpoints.down("sm"));
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());
  const businessName = theme.nameBusiness || NAME_BUSINESS;
  const canPost = dispatch(getRoleCapability("canPost"));
  const { openCalendar } = useContentModals();
  const {
    automations,
    isLoading,
    createAutomation,
    updateAutomation,
    pauseAutomation,
    resumeAutomation,
    renameAutomation,
    setStatusOverride,
  } = usePublishingAutomations();
  const {
    open: paywallOpen,
    status: paywallStatus,
    isLoading: paywallLoading,
    isCheckingOut,
    openPaywall,
    closePaywall,
    checkoutTier,
    blockIfCapacityReached,
    openPaywallFromLimitError,
    allowedPlatforms,
    allowedFrequencies,
  } = useAutomationsAddon();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const heroMetrics = summarizeHeroMetrics(automations);
  const liveEditingAutomation = editingAutomation
    ? automations.find((item) => item.id === editingAutomation.id) ||
      editingAutomation
    : null;

  const onOpenModal = async () => {
    if (await blockIfCapacityReached({ liveCount: automations.length })) return;
    setEditingAutomation(null);
    setModalOpen(true);
  };
  const onCloseModal = () => {
    setModalOpen(false);
    setEditingAutomation(null);
  };

  const onToggleActive = useCallback(
    async (id, active) => {
      if (!canPost) return;
      const item = automations.find((automation) => automation.id === id);
      const copy = buildOptimisticSwitchCopy({
        t,
        kind: OPTIMISTIC_SWITCH_KIND.AUTOMATION,
        name: item?.name || "",
        activate: active,
      });

      await runOptimisticSwitch({
        copy,
        applyOptimistic: () => {
          setStatusOverride((prev) => ({
            ...prev,
            [id]: active,
          }));
        },
        revertOptimistic: () => {
          setStatusOverride((prev) => ({
            ...prev,
            [id]: !active,
          }));
        },
        action: ({ operationId }) => {
          const optimisticSwitch = toOptimisticSwitchPayload({
            operationId,
            copy,
          });
          if (active) {
            return resumeAutomation(id, optimisticSwitch);
          }
          return pauseAutomation(id, optimisticSwitch);
        },
      });
    },
    [
      automations,
      canPost,
      pauseAutomation,
      resumeAutomation,
      setStatusOverride,
      t,
    ]
  );

  const onEditAutomation = useCallback((item) => {
    setEditingAutomation(item);
    setModalOpen(true);
  }, []);

  const onSubmitAutomation = async (payload) => {
    if (editingAutomation) {
      const response = await updateAutomation({
        ...payload,
        automationID: editingAutomation.id,
        expectedVersion:
          liveEditingAutomation?.version ?? editingAutomation.version,
      });
      if (response?.ok) {
        onCloseModal();
      }
      return response;
    }

    const response = await createAutomation(payload);
    if (response?.ok) {
      onCloseModal();
    } else if (openPaywallFromLimitError(response)) {
      return response;
    }
    return response;
  };

  const onCommitName = useCallback(
    (name) => {
      if (!editingAutomation?.id) return { ok: true };
      return renameAutomation(editingAutomation.id, name);
    },
    [editingAutomation?.id, renameAutomation]
  );

  return (
    <>
      <FeatureAddonUnpaidPaywall addonKey={FEATURE_ADDON_KEYS.AUTOMATIONS} />
      <Helmet>
        <title>{`${businessName} - ${t(AUTOMATIONS_PAGE_TITLE)}`}</title>
      </Helmet>

      <ContainerPage hasMarginBottom={false} sx={{ height: "100%" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "minmax(0, 1fr) auto",
            },
            gridTemplateRows: {
              xs: "auto auto auto",
              lg: "auto auto",
            },
            gridTemplateAreas: {
              xs: `
                "title"
                "subtitle"
                "actions"
              `,
              lg: `
                "title actions"
                "subtitle subtitle"
              `,
            },
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            px: { xs: 0, lg: 3 },
            mt: 4,
            gap: 1,
            alignItems: { xs: "flex-start", lg: "center" },
          }}
        >
          <Box sx={{ gridArea: "title", minWidth: 0, maxWidth: "100%" }}>
            <TitlePage sx={{ overflowWrap: "anywhere" }}>
              {t(AUTOMATIONS_PAGE_TITLE)}
            </TitlePage>
          </Box>

          <Box
            sx={{
              gridArea: "subtitle",
              pb: { xs: 1, lg: 0 },
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: "#878383",
                fontSize: 14,
                overflowWrap: "anywhere",
              }}
            >
              {t(AUTOMATIONS_PAGE_DESCRIPTION, { NAME_BUSINESS: businessName })}
            </Typography>
            {!canPost && (
              <Typography variant="body2" sx={{ color: "#9aa1ad", mt: 0.5 }}>
                {t(AUTOMATIONS_NO_PERMISSION)}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              gridArea: "actions",
              display: "flex",
              alignItems: { xs: "stretch", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              width: { xs: "100%", lg: "auto" },
              maxWidth: "100%",
              minWidth: 0,
              justifyContent: { xs: "flex-start", lg: "flex-end" },
            }}
          >
            <Button
              fullWidth={isMobile}
              onClick={onOpenModal}
              color="primary"
              disabled={!canPost}
              startIcon={<AddIcon sx={{ width: 20, height: 20 }} />}
            >
              {t(BUTTON_NEW_AUTOMATION)}
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            px: { xs: 0, lg: 3 },
            mt: 2,
          }}
        >
          <AutomationHeroCard
            businessName={businessName}
            metrics={heroMetrics}
          />

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {automations.map((item) => (
                <AutomationCard
                  key={item.id}
                  item={item}
                  canPost={canPost}
                  onToggleActive={onToggleActive}
                  onEdit={onEditAutomation}
                  onViewCalendar={openCalendar}
                />
              ))}
            </Box>
          )}
        </Box>
      </ContainerPage>

      {modalOpen && (
        <ModalCreateAutomation
          modalOpen={modalOpen}
          onCloseModal={onCloseModal}
          onSubmitAutomation={onSubmitAutomation}
          onCommitName={onCommitName}
          businessName={businessName}
          editingAutomation={editingAutomation}
          liveEditingAutomation={liveEditingAutomation}
          canPost={canPost}
          onToggleActive={onToggleActive}
          allowedPlatforms={allowedPlatforms}
          allowedFrequencies={allowedFrequencies}
          onLockedCapability={() => openPaywall(paywallStatus)}
        />
      )}

      <FeatureAddonPaywallDialog
        open={paywallOpen}
        onClose={closePaywall}
        status={paywallStatus}
        addonKey={FEATURE_ADDON_KEYS.AUTOMATIONS}
        isLoading={paywallLoading}
        isCheckingOut={isCheckingOut}
        onCheckoutTier={checkoutTier}
      />
    </>
  );
};

export default Automations;
